<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end form-offcanvas"
    tabindex="-1"
    id="offcanvaInfo"
    ref="offcanvaInfo"
    data-bs-backdrop="static"
  >
    <!-- data-bs-backdrop="static" -->
    <div class="offcanvas-header border-bottom bg-white py-3">
      <h6 class="offcanvas-title fw-bold mb-0 d-flex align-items-center gap-2">
        <i class="fa-solid fa-industry text-theme" />
        {{ $t('Batch Detail')  }}
      </h6>
      <button
        type="button"
        style="font-size: small;"
        class="btn-close shadow-none"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>
    <div
      class="offcanvas-body"
      v-if="loading"
    >
      <placeholder-content :is-load="true" />
    </div>
    <div
      class="offcanvas-body bg-body-secondary p-3"
      v-else
    >
      <!-- ───── Hero ───── -->
      <div class="form-card">
        <div class="px-3 py-3">
          <div class="fw-bold d-flex align-items-center flex-wrap gap-2" style="font-size: 18px;">
            {{ data.name + ': ' + data.document_no }}
            <span
              :class="['badge', viewpoints.find((viewpoint) => viewpoint.value == data.status).bgc]"
              style="text-transform: capitalize"
            >
              {{ $t(data.status) }}
            </span>
          </div>
          <div class="text-secondary small mt-2">
            {{ $t('Last Update') + ': ' + formatDateTime(data.updated_at) }}
          </div>
        </div>
      </div>

      <!-- ───── ข้อมูล Batch ───── -->
      <div class="form-card">
        <div class="form-section-header">
          <i class="fa-solid fa-circle-info" />
          <span>{{ $t('Batch Information') }}</span>
        </div>
        <div class="px-3 pt-3">
          <div class="row" style="font-size: 13px;">
            <div class="col-md-3 mb-3">
              <b>
                <!-- <i class="fa-solid fa-circle-info me-1" /> -->
                {{ $t('Branch') + ': ' }}
              </b>
              {{ data.branch }}
            </div>
            <div class="col-md-9 mb-3">
              <b>
                <!-- <i class="fa-solid fa-coins me-1" /> -->
                {{ $t('Batch Size') + ': ' }}
              </b>
              {{ formatDigit(data.batch_multiplier) + $t('_BatchSize.x') }}
            </div>
            <div class="col-md-3 mb-3">
              <b>
                <!-- <i class="fa-solid fa-hashtag me-1" /> -->
                {{ $t('Create By') + ': ' }}
              </b>
              {{ data.created_by }}<br>
              <b>
                <!-- <i class="fa-solid fa-hashtag me-1" /> -->
                {{ $t('Created At') + ': ' }}
              </b>
              {{ formatDateTime(data.created_at) }}
            </div>
            <div
              v-if="data.confirmed_by"
              class="col-md-3 mb-3"
            >
              <b>
                {{ $t('Confirmed By') + ': ' }}
              </b>
              {{ data.confirmed_by }}<br>
              <b>
                {{ $t('Confirmed At') + ': ' }}
              </b>
              {{ formatDateTime(data.confirmed_at) }}
            </div>
            <div
              v-if="data.progressing_by"
              class="col-md-3 mb-3"
            >
              <b>
                {{ $t('Progressing By') + ': ' }}
              </b>
              {{ data.progressing_by }}<br>
              <b>
                {{ $t('Started At') + ': ' }}
              </b>
              {{ formatDateTime(data.progressing_at) }}
            </div>
            <div
              v-if="data.completed_by"
              class="col-md-3 mb-3"
            >
              <b>
                {{ $t('Completed By') + ': ' }}
              </b>
              {{ data.completed_by }}<br>
              <b>
                {{ $t('Completed At') + ': ' }}
              </b>
              {{ formatDateTime(data.completed_at) }}
            </div>
            <div
              v-if="data.cancelled_by"
              class="col-md-3 mb-3"
            >
              <b>
                {{ $t('Cancelled By') + ': ' }}
              </b>
              {{ data.cancelled_by }}<br>
              <b>
                {{ $t('Cancelled At') + ': ' }}
              </b>
              {{ formatDateTime(data.cancelled_at) }}
            </div>
          </div>
          <div
            v-if="data.reason"
            class="row"
            style="font-size: 13px;"
          >
            <div
              class="col-md-3 mb-3"
            >
              <b>
                {{ $t('Reason') + ': ' }}
              </b>
              {{ data.reason }}
            </div>
          </div>
        </div>
      </div>
      <!-- ───── Inputs ───── -->
      <div class="form-card form-card-overflow">
        <div class="form-section-header">
          <i class="fa-solid fa-arrow-right-to-bracket" />
          <span>
            <template v-if="data.status === 'draft'">{{ $t('Inputs: Planned Ingredient Usage') }}</template>
            <template v-else-if="['pending', 'progressing'].includes(data.status)">{{ $t('Inputs: Record Actual Ingredient Usage') }}</template>
            <template v-else>{{ $t('Inputs: Actual Ingredient Usage Summary') }}</template>
          </span>
        </div>
        <div class="px-3 pt-3">
          <div
            class="text-secondary mb-2"
            style="font-size: 13px;"
            v-if="data.status === 'draft'"
          >
            {{ $t('This is a draft plan. Actual usage cannot be recorded yet.') }}
          </div>
          <div
            class="text-secondary mb-2"
            style="font-size: 13px;"
            v-else-if="['pending', 'progressing'].includes(data.status)"
          >
            {{ $t(`Click '📝 Record Lots' to specify actual quantities and lot details from inventory.`) }}
          </div>
          <div
            class="bg-light d-flex justify-content-between align-items-center mt-3 px-2 py-1 rounded-2"
            style="font-size: 13px;"
            v-if="data.status !== 'draft'"
          >
            <span class="text-secondary">
              <i class="fa-solid fa-coins me-1" />
              {{ $t('Total cost used') }}
            </span>
            <span>
              <b :class="{ 'text-danger': parseFloat(estimatedActualTotalInputCost) > parseFloat(data.planned_total_input_cost) }">
                {{ formatNumberTwoDigit(estimatedActualTotalInputCost) }} {{ $t('THB') }}
              </b>
              <span class="text-secondary">
                / {{ $t('Planned') }} {{ formatNumberTwoDigit(data.planned_total_input_cost) }} {{ $t('THB') }}
              </span>
            </span>
          </div>
          <div class="mt-2">
            <view-input-form
              v-for="(input, index) in data.inputs"
              :key="input.id"
              :index="index"
              :input="input"
            />
            <div
              v-if="data.inputs.length == 0"
              class="text-center text-secondary py-3"
              style="font-size: 13px;"
            >
              {{ $t('No Available Data') }}
            </div>
            <div
              v-if="data.status === 'progressing'"
              class="add-input-zone mt-2"
              id="addInputZone"
            >
              <select2
                :key="addInputSelect2.rerender"
                @select="handleAddInputSelected"
                :options="addInputSelect2.options"
                :settings="addInputSelect2.setting"
                :placeholder="'+ ' + $t('Add Unplanned Ingredient') + '...'"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- ───── Outputs ───── -->
      <div class="form-card">
        <div class="form-section-header">
          <i class="fa-solid fa-arrow-right-from-bracket" />
          <span>
            <template v-if="data.status === 'draft'">{{ $t('Outputs: Planned Product Yield') }}</template>
            <template v-else-if="['pending', 'progressing'].includes(data.status)">{{ $t('Outputs: Record Actual Product Yield') }}</template>
            <template v-else>{{ $t('Outputs: Final Product Yield') }}</template>
          </span>
        </div>
        <div class="px-3 pt-3">
          <div
            class="text-secondary mb-2"
            style="font-size: 13px;"
            v-if="data.status === 'draft'"
          >
            {{ $t('This is a draft plan. Final yield cannot be entered yet.') }}
          </div>
          <div
            class="text-secondary mb-2"
            style="font-size: 13px;"
            v-else-if="['pending', 'progressing'].includes(data.status)"
          >
            {{ $t(`Enter the final quantities produced.`) }}
          </div>
          <div
            class="overflow-x-auto"
          >
            <table class="table table-sm mt-3">
              <thead class="table-light">
                <tr>
                  <th
                    style="min-width: 200px;"
                  >
                    {{ $t('Product / Inventory') }}
                  </th>
                  <th
                    class="text-end"
                    style="min-width: 150px;"
                  >
                    {{ $t('Planned Quantity') }}
                  </th>
                  <th
                    class="text-end"
                    style="min-width: 180px;max-width: 180px; width: 180px;"
                    v-if="['pending', 'progressing'].includes(data.status)"
                  >
                    {{ $t('Actual Quantity (Input)') }}
                  </th>
                  <th
                    class="text-end"
                    style="min-width: 180px;max-width: 180px; width: 180px;"
                    v-if="['completed', 'cancelled'].includes(data.status)"
                  >
                    {{ $t('Actual Qty (Final)') }}
                  </th>
                  <th>
                    {{ $t('Unit') }}
                  </th>
                </tr>
              </thead>
              <tbody
                class="align-baseline"
              >
                <tr
                  v-for="(output, index) in data.outputs"
                >
                  <view-output-form
                    :index="index"
                    :output="output"
                  />
                </tr>
                <tr
                  v-if="data.outputs.length == 0"
                >
                  <td
                    colspan="3"
                    class="text-center"
                  >
                    {{ $t('No Available Data') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <div
      class="bg-light border-top modal-footer px-3 py-2"
      v-if="!loading && data.status !== 'completed'"
    >
      <button
        type="button"
        class="btn-sm btn btn-theme ms-2"
        v-if="data.status === 'progressing'"
        @click="handleComplete()"
        :disabled="v$.$invalid"
      >
        <i class='fa-solid fa-floppy-disk' />
        {{ $t('Complete Batch') }}
      </button>
      <button
        type="button"
        class="btn-sm btn btn-theme ms-2"
        @click="handleStart()"
        v-if="data.status === 'pending'"
      >
        <i class='fa-solid fa-play' />
        {{ $t('Start Production') }}
      </button>
      <button
        type="button"
        class="border-dark-subtle btn-sm btn btn-light ms-2"
        @click="handleCancel()"
        v-if="['pending','progressing'].includes(data.status)"
      >
        <i class='fa-solid fa-ban' />
        {{ $t('Cancel Batch') }}
      </button>
      <button
        type="button"
        class="btn-sm btn btn-theme ms-2"
        @click="handleConfirm()"
        v-if="data.status === 'draft'"
      >
        <i class='fa-solid fa-check' />
        {{ $t('Submit Plan') }}
      </button>
      <button
        type="button"
        class="btn-sm btn btn-danger ms-2"
        @click="handleDelete()"
        v-if="data.status === 'draft'"
      >
        <i class='fa-solid fa-trash-can' />
        {{ $t('Delete') }}
      </button>
      <!-- <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="border-dark-subtle btn-sm btn btn-light ms-2"
      >
        {{ $t('Cancel') }}
      </button> -->
    </div>
  </div>
  <modal-record-lot
    v-if="modalRecordLotForm.open"
    :name="modalRecordLotForm.name"
    :unit="modalRecordLotForm.unit"
    :id="modalRecordLotForm.id"
    @update-usage="handleUpdateUsage"
  />
  <modal-reverse-record
    v-if="modalReverseRecordForm.open"
    :name="modalReverseRecordForm.name"
    :unit="modalReverseRecordForm.unit"
    :id="modalReverseRecordForm.id"
    @reverse-usage="handleReverseUsage"
  />
</template>

<script lang="js">
import { useVuelidate } from '@vuelidate/core';
import Select2 from 'vue3-select2-component';
import { InventoryProductionBatches } from '@/services/endpoint/inventory-production-batches.service';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import Ladda from '@/plugins/Ladda.vue';
import moment from "moment";
import numeral from 'numeral';
import { distance } from '@/store/date-fns'
import Popper from "vue3-popper";
import ViewInputForm from './ViewInputForm.vue';
import ViewOutputForm from './ViewOutputForm.vue';
import ModalRecordLot from './ModalRecordLot.vue';
import ModalReverseRecord from './ModalReverseRecord.vue';
import { AUTH } from '@/services/endpoint/_constants';
import signature from '@/services/signature';
import validation from "./assets/template/validation-view-form";

export default {
  setup: () => ({ v$: useVuelidate() }),
  validations() {
    return validation(this.data)
  },
  props: {
    id: {
      type: Number,
      default: 0
    }
  },
  data() {
    let vm = this;
    return {
      viewpoints: [
        {
          value: 'all',
          text: 'All',
          bgc: 'bg-theme text-white'
        },
        {
          value: 'draft',
          text: 'Draft',
          bgc: 'bg-secondary text-white'
        },
        {
          value: 'pending',
          text: 'Pending',
          bgc: 'bg-warning text-dark'
        },
        {
          value: 'progressing',
          text: 'Progressing',
          bgc: 'bg-info text-dark'
        },
        {
          value: 'completed',
          text: 'Completed',
          bgc: 'bg-success text-white'
        },
        {
          value: 'cancelled',
          text: 'Cancelled',
          bgc: 'bg-danger text-white'
        }
      ],
      isHovered: false,
      loadingCard: true,
      loading: true,
      data: {},
      canSubmit: true,
      modalRecordLotForm: {
        open: false,
        id: '',
        name: '',
        unit: ''
      },
      modalReverseRecordForm: {
        open: false,
        id: '',
        name: '',
        unit: ''
      },
      addInputSelect2: {
        rerender: 0,
        options: [],
        setting: {
          dropdownParent: '#addInputZone',
          minimumResultsForSearch: true,
          multiple: false,
          language: {
            loadingMore: function (e) {
              return '<i class="fa-solid fa-spinner fa-spin" />'
            }
          },
          templateResult: function (item) {
            if (!item.id) return item.text;
            const stock = parseFloat(item.stock || 0);
            return $(
              '<div class="py-1">' +
                '<div style="font-size: 13px; font-weight: 600;">' + item.name + '</div>' +
                '<div style="font-size: 11px; color: ' + (stock > 0? '#6c757d': '#dc3545') + ';">' + vm.$t('In stock') + ': ' + numeral(item.stock || 0).format('0,0.00') + ' ' + vm.$t(item.unit) + '</div>' +
              '</div>'
            );
          },
          ajax: {
            beforeSend: function (request) {
              request.setRequestHeader('Authorization', 'Bearer ' + AUTH.TOKEN);
            },
            url: function () {
              return AUTH.URL + '/v1/inventory-production-batches/' + vm.id + '/inputs';
            },
            data: function (params) {
              var query = {
                search: params.term,
                page: params.page || 1,
                limit: 10
              };
              return signature($.param(query)).substring(1);
            },
            processResults: function (data, params) {
              let option = data.data;
              let pageCurrent = data.meta.current_page;
              option = option.map(function(item) {
                return {
                  id: item.id,
                  text: item.name,
                  name: item.name,
                  stock: item.stock,
                  unit_id: item.unit_id,
                  unit: item.unit
                }
              });
              return {
                results: option,
                pagination: {
                  more: (pageCurrent * 10) < data.meta.total
                }
              }
            }
          }
        }
      }
    }
  },
  components: {
    Select2,
    PlaceholderContent,
    Ladda,
    Popper,
    ViewInputForm,
    ViewOutputForm,
    ModalRecordLot,
    ModalReverseRecord
  },
  methods: {
    handleUpdateUsage(data) {
      const target = this.data.inputs.find(input => input.id == data.id);

      if (target) {
        target.actual_quantity = parseFloat(target.actual_quantity) + data.used;
        target.remaining_quantity = parseFloat(target.remaining_quantity) - data.used;
        target.actual_total_cost = parseFloat(target.actual_total_cost) + data.actualTotalCost;
      }
    },
    handleReverseUsage(data) {
      const target = this.data.inputs.find(input => input.id == data.id);

      if (target) {
        target.actual_quantity = parseFloat(target.actual_quantity) - data.reversed;
        target.remaining_quantity = parseFloat(target.remaining_quantity) + data.reversed;
        target.actual_total_cost = parseFloat(target.actual_total_cost) - data.reversedTotalCost;
      }
    },
    async handleAddInputSelected(inventory) {
      let self = this;
      let html = '<b>' + inventory.name + '</b><br>';
      html += '<small>' + this.$t('In stock') + ': <span style="color:' + (parseFloat(inventory.stock) > 0? '#198754': '#dc3545') + ';">' + numeral(inventory.stock || 0).format('0,0.00') + ' ' + this.$t(inventory.unit) + '</span></small>';
      html += '<br><br><small style="color: #999;">' + this.$t('This ingredient is not in the recipe. It will be added with planned quantity 0 and counted into the actual cost of this batch.') + '</small>';
      this.$swal({
        icon: 'question',
        iconColor: '#0d6efd',
        title: this.$t('Add Unplanned Ingredient') + '?',
        html: html,
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: this.$t('Yes, Add it'),
        buttonsStyling: false,
        reverseButtons: true,
        focusCancel: true,
        customClass: {
          confirmButton: 'btn btn-theme',
          cancelButton: 'btn btn-danger me-2',
        },
        focusConfirm: false,
        showLoaderOnConfirm: true,
        preConfirm: async function () {
          return InventoryProductionBatches.addInput(self.id, {
            inventory_id: inventory.id
          }).then(() => {
            self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Add Ingredient Success!'));
            self.loading = true;
            self.getInfoData();
          }).catch((error) => {
            if (error?.response?.data?.code === "VALIDATION") {
              self.$swal.showValidationMessage(self.$t(error?.response?.data?.message));
            } else {
              self.$toast.warn(self.$t(error?.response?.data?.message || 'Something went wrong.'));
            }
          });
        },
      }).then(() => {
        self.addInputSelect2.rerender++;
      });
    },
    async handleDeleteInput(input) {
      let self = this;
      let html = '<b>' + input.name + '</b><br><small>' + this.$t('No usage has been recorded on this line. It will be removed from the batch.') + '</small>';
      if (!input.is_unplanned) {
        html += '<br><small style="color: #d9534f;">' + this.$t('This is a planned ingredient from the recipe. Remove it only if it was not used in this production.') + '</small>';
      }
      this.$swal({
        icon: "warning",
        iconColor: "#FF7043",
        title: this.$t('Remove Ingredient') + '?',
        html: html,
        showCancelButton: true,
        cancelButtonText: this.$t("Cancel"),
        confirmButtonText: this.$t("Yes, Remove it"),
        buttonsStyling: false,
        reverseButtons: true,
        customClass: {
          confirmButton: "btn btn-theme",
          cancelButton: "btn btn-danger me-2",
        },
        focusConfirm: false,
        showLoaderOnConfirm: true,
        preConfirm: async function () {
          return InventoryProductionBatches.deleteInput(self.id, input.id)
            .then(() => {
              self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Delete Success!'));
              self.loading = true;
              self.getInfoData();
            })
            .catch((error) => {
              if (error?.response?.data?.code === "VALIDATION") {
                self.$swal.showValidationMessage(self.$t(error?.response?.data?.message));
              } else {
                self.$toast.warn(error?.response?.data?.message);
              }
            });
        },
      });
    },
    async handleAutoFill(input) {
      let self = this;

      // 1) ขอ preview จาก BE (BE คำนวณ FEFO->FIFO เองทั้งหมด)
      let preview;
      try {
        const queryString = '?' + $.param({ inventory_production_batch_input_id: input.id });
        const res = await InventoryProductionBatches.getAutoFill(this.id, queryString);
        preview = res.data.data;
      } catch (err) {
        this.$toast.warn(this.$t(err?.response?.data?.message) || this.$t('Something went wrong.'));
        return;
      }

      if (!preview.lines || preview.lines.length === 0 || parseFloat(preview.filled) <= 0) {
        this.$toast.warn(this.$t('No stock available to auto-fill.'));
        return;
      }

      // 2) สร้างกล่องยืนยันจากรายการ lot ที่ BE เลือกให้
      let rows = preview.lines.map(line =>
        '<tr>' +
          '<td style="text-align:left;padding:2px 0;">' + line.lot_number +
            '<br><small style="color:#999;">' + (line.expiry_date ? ('exp ' + moment(line.expiry_date).format('YYYY-MM-DD')) : '-') + '</small>' +
          '</td>' +
          '<td style="text-align:right;padding:2px 0;">' + numeral(line.quantity).format('0,0.00') + ' ' + self.$t(line.unit) + '</td>' +
        '</tr>'
      ).join('');

      let html = '<div style="font-size:13px;text-align:left;">' + this.$t('The system will deduct the following lots (soonest expiry first):') + '</div>';
      html += '<table style="width:100%;font-size:13px;margin-top:8px;"><tbody>' + rows + '</tbody></table>';
      html += '<div style="text-align:right;margin-top:8px;">' + this.$t('Total Quantity') + ': <b style="font-size:1.15em;color:#1890ff;">' + numeral(preview.filled).format('0,0.00') + ' ' + this.$t(preview.unit) + '</b></div>';
      if (preview.partial) {
        html += '<div style="color:#d9534f;margin-top:6px;font-size:12px;text-align:left;">⚠ ' + this.$t('Not enough stock in lots. Filled only') + ' ' + numeral(preview.filled).format('0,0.00') + ' / ' + numeral(preview.needed).format('0,0.00') + ' ' + this.$t(preview.unit) + '</div>';
      }
      html += '<div style="margin-top:8px;font-size:11px;color:#999;text-align:left;">' + this.$t('Inventory will be reduced immediately.') + '</div>';

      // 3) ยืนยัน -> commit ที่ BE (BE คำนวณใหม่ตอน commit กัน race)
      this.$swal({
        icon: 'question',
        iconColor: '#0d6efd',
        title: this.$t('Auto-fill Usage') + ': ' + preview.name,
        html: html,
        showCancelButton: true,
        cancelButtonText: this.$t('Review Again'),
        confirmButtonText: this.$t('Yes, Deduct Now'),
        buttonsStyling: false,
        reverseButtons: true,
        focusCancel: true,
        customClass: {
          confirmButton: 'btn btn-theme',
          cancelButton: 'btn btn-danger me-2',
        },
        focusConfirm: false,
        showLoaderOnConfirm: true,
        preConfirm: async function () {
          return InventoryProductionBatches.autoFill(self.id, { inventory_production_batch_input_id: input.id })
            .then((response) => {
              const result = response.data.data;
              self.handleUpdateUsage({
                id: input.id,
                used: parseFloat(result.filled),
                actualTotalCost: parseFloat(result.total_cost)
              });
              self.$toast.success('<B>' + self.$t('Success') + '</B>\n' + self.$t('Record Batches Success!'));
            })
            .catch((error) => {
              if (error?.response?.status === 403) {
                self.$swal.update({
                  title: self.$t('Access Denied'),
                  confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t('Try again'),
                  text: self.$t('You do not have permission to access this page.')
                });
                self.$swal.showValidationMessage(self.$t(error?.response?.data?.message));
              } else if (error?.response?.data?.code === 'VALIDATION') {
                self.$swal.showValidationMessage(self.$t(error?.response?.data?.message));
              } else {
                self.$toast.warn(error?.response?.data?.message);
              }
            });
        },
      });
    },
    bodyItemClassNameFunction(column, rowNumber) {
      let className = 'border-0';
      const columnStart = ['name'];
      const columnCenter = [];
      const columnEnd = ['planned_quantity'];
      if (columnStart.includes(column)) {
        className += ' text-start';
      }
      if (columnCenter.includes(column)) {
        className += ' text-center';
      }
      if (columnEnd.includes(column)) {
        className += ' text-end';
      }
      return className;
    },
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
    formatDigit(value) {
      if ((value < 0.01) && value != 0) {
        return numeral(value).format("0,0.0000");
      }
      return numeral(value).format("0,0.00");
    },
    formatNumberDigit(value) {
      return numeral(value).format("0,0.0000");
    },
    formatNumberTwoDigit(value) {
      return numeral(value).format("0,0.00");
    },
    async getInfoData() {
      await InventoryProductionBatches.getInfo(this.id).then(res => {
        this.data = res.data.data;
        this.data.outputs = res.data.data.outputs.map(function(output) {
          return {
            id: output.id,
            inventory_id: output.inventory_id,
            name: output.name,
            planned_quantity: output.planned_quantity,
            cost_allocation_percent: output.cost_allocation_percent,
            actual_quantity: parseFloat(output.actual_quantity) == 0? '': parseFloat(output.actual_quantity),
            actual_cost_per_unit: output.actual_cost_per_unit,
            actual_allocated_total_cost: output.actual_allocated_total_cost,
            unit_id: output.unit_id,
            unit: output.unit
          }
        });
        this.canSubmit = (this.data.inputs.find((input) => parseFloat(input.remaining_quantity) < parseFloat(input.planned_quantity)))? false: true;
        this.loading = false;
      }).catch(err => {
			  throw err;
      });
    },
    formatDate(value) {
      return moment(value).format('DD/MM/YYYY');
    },
    formatDateTime(value) {
      return moment(value).format('YYYY-MM-DD HH:mm:ss');
    },
    dateDistance(value) {
      return distance(value);
    },
    async handleDelete() {
      try {
        let self = this;
        let title = this.$t("Delete Draft Plan") + '?';
        let html = this.$t('This draft will be permanently deleted. You cannot undo this action!');
        let confirmButtonText =
          '<i class="fas fa-check-circle me-1"></i>' + this.$t("Yes, delete it!");
        this.$swal({
          icon: "warning",
          iconColor: "#FF7043",
          title: title,
          html: html,
          showCancelButton: true,
          cancelButtonText: this.$t("Cancel"),
          confirmButtonText: confirmButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-theme me-2",
            cancelButton: "btn btn-danger",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          preConfirm: async function () {
            return InventoryProductionBatches.delete(self.id)
              .then((response) => {
                self.$parent.fetchData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Delete Success!'));
                $(self.$refs.offcanvaInfo).offcanvas('hide');
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else if (error?.response?.data?.code === "VALIDATION") {
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        });
      } catch (error) {
      }
    },
    async handleStart() {
      try {
        let self = this;
        let title = this.$t("Start Production on the Floor") + '?';
        let html = this.$t(`Status will change to 'Progressing'. You will be able to start recording inventory usage.`);
        let confirmButtonText = '<i class="fas fa-play me-1"></i>' + this.$t(`Yes, Let's Start`)+ '!';
        let icon = "question";
        this.$swal({
          icon: icon,
          iconColor: "#FF7043",
          title: title,
          // text: "You will not be able to undo this action.",
          html: html,
          showCancelButton: true,
          cancelButtonText: this.$t("Not yet"),
          confirmButtonText: confirmButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-theme me-2",
            cancelButton: "btn btn-danger",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          focusCancel: true,
          preConfirm: async function () {
            return InventoryProductionBatches.start(self.id)
              .then(async (response) => {
                await self.$parent.fetchData();
                await self.getInfoData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Start Success!'));
                // $(self.$refs.offcanvaInfo).offcanvas('hide');
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else if (error?.response?.data?.code === "VALIDATION") {
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        });
      } catch (error) {
      }
    },
    async handleConfirm() {
      try {
        let self = this;
        let title = this.$t("Confirm Submission") + '?';
        let html = this.$t(`This production plan will be set to 'Pending' and sent to the kitchen staff.`);
        let confirmButtonText = '<i class="fas fa-check-circle me-1"></i>' + this.$t("Yes, Submit Plan");
        let icon = "question";
        if (!this.canSubmit) {
          title = this.$t("Warning: Insufficient Stock");
          html = this.$t(`Some ingredients currently have low stock. Submitting this plan might cause issues or delays in the kitchen. Do you still want to proceed`) + '?';
          confirmButtonText = '<i class="fas fa-check-circle me-1"></i>' + this.$t("Proceed Anyway (Risk)");
          icon = "warning";
        }
        this.$swal({
          icon: icon,
          iconColor: "#FF7043",
          title: title,
          // text: "You will not be able to undo this action.",
          html: html,
          showCancelButton: true,
          cancelButtonText: this.$t("Cancel"),
          confirmButtonText: confirmButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-theme me-2",
            cancelButton: "btn btn-danger",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          focusCancel: true,
          preConfirm: async function () {
            return InventoryProductionBatches.confirm(self.id)
              .then((response) => {
                self.$parent.fetchData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Confirm Submission Success!'));
                $(self.$refs.offcanvaInfo).offcanvas('hide');
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else if (error?.response?.data?.code === "VALIDATION") {
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        });
      } catch (error) {
      }
    },
    async handleCancel() {
      try {
        let self = this;
        let title = this.$t("Cancel Production") + '?';
        let html = this.$t(`No stock has been deducted yet. This is a safe cancellation.`) + '<br>' + this.$t(`Please provide a reason for our records`) + ':';
        let confirmButtonText = this.$t("Yes, Cancel Batch");
        let cancelButtonText = this.$t("No, Keep it");
        if (this.data.status === 'progressing') {
          title = this.$t("Abort Production") + '?';
          html = '⚠️ <b>' + this.$t(`Critical Warning`) + ': </b><br>';
          html+= this.$t('Any ingredients already recorded (used) will be marked as') + ' <b>' + this.$t('WASTE') + '</b> ' + this.$t('and will NOT be returned to inventory.') + '<br><br>' + this.$t('Please provide a reason for aborting this job') + ':';
          confirmButtonText = this.$t("Yes, Abort & Mark Waste");
          cancelButtonText = this.$t("No, Continue Production");
        }
        this.$swal({
          icon: "warning",
          input: 'textarea', // พระเอกของเรา
          inputLabel: self.$t('Reason for cancellation (Required)')+ ':',
          inputPlaceholder: self.$t('Type your reason here (e.g., Wrong recipe, Machine failure)')+ '...',
          inputAttributes: {
            'rows': 3 // ความสูงของ textarea
          },
          target: document.getElementById('offcanvaInfo'),
          iconColor: "#FF7043",
          title: title,
          html: html,
          showCancelButton: true,
          cancelButtonText: cancelButtonText,
          confirmButtonText: confirmButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-theme me-2",
            cancelButton: "btn btn-danger",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          inputValidator: (value) => {
            if (!value || value.trim() === '') {
              return self.$t('You must provide a reason to proceed') + '!';
            }
          },
          preConfirm: async function (value) {
            let params = {
              reason: value
            };
            return InventoryProductionBatches.cancel(self.id, params)
              .then((response) => {
                self.$parent.fetchData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Cancel Batches Success!'));
                $(self.$refs.offcanvaInfo).offcanvas('hide');
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else if (error?.response?.data?.code === "VALIDATION") {
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        });
      } catch (error) {
      }
    },
    applyOutputCosts() {
      const actualTotalInputCost = this.estimatedActualTotalInputCost;
      this.data.outputs = this.data.outputs.map((output) => {
        const actualQuantity = parseFloat(output.actual_quantity);
        const totalCost = (actualTotalInputCost * (output.cost_allocation_percent / 100)).toFixed(4);
        return {
          ...output,
          actual_cost_per_unit: actualQuantity > 0 ? (totalCost / actualQuantity).toFixed(4) : '0.0000',
          actual_allocated_total_cost: totalCost,
        };
      });
    },
    setParamsBeforeSendComplete() {
      // ส่งเฉพาะ key ที่ CompleteMiddleware อนุญาต (BE reject key แปลกปลอมด้วย ERROR_KEY_NOT_ALLOWED)
      // อย่าสร้าง payload จาก this.data ทั้งก้อน เพราะ field ใหม่ใน InfoResource จะหลุดไปด้วย
      return {
        actual_total_input_cost: this.data.actual_total_input_cost,
        outputs: this.data.outputs.map(function(output) {
          return {
            id: output.id,
            inventory_id: output.inventory_id,
            unit_id: output.unit_id,
            actual_quantity: output.actual_quantity,
            actual_cost_per_unit: output.actual_cost_per_unit,
            actual_allocated_total_cost: output.actual_allocated_total_cost,
          }
        })
      };
    },
    async handleComplete() {
      this.v$.$touch();
      if(this.v$.$invalid) {
        return;
      }
      try {
        let self = this;
        let title = this.$t("Finalize and Complete Batch") + '?';
        let html = this.$t('Outputs will be received into stock and this batch will be closed permanently.');
        let confirmButtonText = this.$t(`Yes, Complete Batch`)+ '!';
        let icon = "warning";
        const underRecorded = this.data.inputs.filter((input) => !input.is_unplanned && parseFloat(input.actual_quantity) < parseFloat(input.planned_quantity) - 0.000001);
        if (underRecorded.length > 0) {
          html += '<div style="text-align:left;margin-top:10px;font-size:13px;">⚠ <b>' + this.$t('Some planned ingredients are not fully recorded') + ':</b></div>';
          html += '<ul style="text-align:left;font-size:13px;margin:6px 0 0 0;">';
          underRecorded.forEach((input) => {
            html += '<li>' + input.name + ' — ' + this.formatDigit(input.actual_quantity) + ' / ' + this.formatDigit(input.planned_quantity) + ' ' + this.$t(input.unit) + '</li>';
          });
          html += '</ul>';
          html += '<div style="text-align:left;font-size:12px;color:#999;margin-top:6px;">' + this.$t('Actual cost will include only what was recorded.') + '</div>';
        }
        this.data.actual_total_input_cost = this.estimatedActualTotalInputCost;
        this.applyOutputCosts();
        this.$swal({
          icon: icon,
          iconColor: "#FF7043",
          title: title,
          // text: "You will not be able to undo this action.",
          html: html,
          showCancelButton: true,
          cancelButtonText: this.$t("Wait, let me check"),
          confirmButtonText: confirmButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-theme",
            cancelButton: "btn btn-danger me-2",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          reverseButtons: true,
          focusCancel: true,
          preConfirm: async function () {
            return InventoryProductionBatches.complete(self.id, self.setParamsBeforeSendComplete())
              .then((response) => {
                self.$parent.fetchData();
                // self.getInfoData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Complete Success!'));
                $(self.$refs.offcanvaInfo).offcanvas('hide');
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(
                    error?.response?.data?.message
                  )
                }
              })
          },
        });
      } catch (error) {
      }
    }
  },
  async mounted() {
    $(this.$refs.offcanvaInfo).offcanvas('show');

    $(this.$refs.offcanvaInfo).on('shown.bs.offcanvas',async () => {
      try {
        this.loading = true;
        await this.getInfoData();
      } catch (err) {
      }
    });

    $(this.$refs.offcanvaInfo).on('hidden.bs.offcanvas', () => {
      this.$parent.viewForm.open = false;
    });
  },
  computed: {
    estimatedActualTotalInputCost() {
      return (this.data.inputs.reduce((sum, value) => {
        return sum + (value.actual_total_cost*1);
      }, 0)).toFixed(4);
    },
  },
  watch: {
  }
}
</script>

<style scoped>
/* การ์ด Inputs: ปล่อยให้ dropdown ของโซนเพิ่มวัตถุดิบทะลุขอบการ์ดได้ (form-card ปกติ overflow: hidden จะตัด dropdown ทิ้ง) */
.form-card.form-card-overflow {
  overflow: visible;
}
/* ชดเชยมุมโค้งบนของ header ที่เคยพึ่ง overflow: hidden ของการ์ด */
.form-card.form-card-overflow .form-section-header {
  border-top-left-radius: 0.75rem;
  border-top-right-radius: 0.75rem;
}

/* add-row: ตัว select2 เองคือกรอบเส้นประ — dropdown จึงกว้างเท่ากรอบพอดี */
.add-input-zone {
  position: relative; /* จุดยึด dropdownParent เท่านั้น ไม่มีหน้าตาของตัวเอง */
}
.add-input-zone :deep(.select2-container) {
  width: 100% !important;
}
.add-input-zone :deep(.select2-selection--single) {
  height: 38px !important;
  border: 1.5px dashed #d4d8dd !important;
  border-radius: 0.6rem !important;
  background-color: #fafbfc !important;
  box-shadow: none !important;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.add-input-zone :deep(.select2-selection--single:hover),
.add-input-zone :deep(.select2-container--open .select2-selection--single) {
  border-color: #E96C15 !important; /* $theme-color */
  background-color: #fff !important;
}
.add-input-zone :deep(.select2-selection__rendered) {
  line-height: 35px !important;
  padding-left: 0.75rem !important;
  font-size: 13px;
  color: #6c757d !important;
}
.add-input-zone :deep(.select2-selection__placeholder) {
  color: #8a929b !important;
}
.add-input-zone :deep(.select2-selection__arrow) {
  height: 35px !important;
  right: 8px;
}

/* dropdown ให้เข้าชุดกับการ์ด */
.add-input-zone :deep(.select2-dropdown) {
  border: 1px solid #e4e7eb;
  border-radius: 0.5rem;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  margin-top: 6px;
}
.add-input-zone :deep(.select2-search--dropdown) {
  padding: 0.5rem;
}
.add-input-zone :deep(.select2-search--dropdown .select2-search__field) {
  border: 1px solid #dee2e6;
  border-radius: 0.4rem;
  padding: 0.35rem 0.6rem;
  font-size: 13px;
  outline: none;
}
.add-input-zone :deep(.select2-search--dropdown .select2-search__field:focus) {
  border-color: #E96C15; /* $theme-color */
}
.add-input-zone :deep(.select2-results__option) {
  padding: 0.3rem 0.75rem;
}
.add-input-zone :deep(.select2-results__option--highlighted),
.add-input-zone :deep(.select2-results__option--highlighted[aria-selected]) {
  background-color: #fdeee2 !important; /* theme ส้มอ่อน — คงสีตัวอักษร inline (แดง/เทา) ให้อ่านออก */
  color: inherit !important;
}
</style>
